import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TreeSelect } from "primereact/treeselect";
import DatePicker from "react-datepicker";
import addDays from "date-fns/addDays";
import {
  colorBackground,
  colorFieldsBorder,
  colorTextAdditional,
  colorAccent,
  colorLightGray,
  sidenavColor,
  colorHover,
} from "../../../utils/palette";
import { borderRadius } from "../../../utils/styles";
import { useAuth } from "../../../contexts/AuthContext";
import AssetDropdown from "../../../shared/components/AssetDropdown";
import useClickAway from "../../../shared/custom-hooks/useClickAway";
import { CustomInput } from "../../../components/Reminders/MaintenanceReminders/MaintenanceRemindersForm";
import { FormGroupLabel } from "../../../shared/components/form/FormElements";
import { AddFormButton } from "../../../shared/components/TableElements";
import { LS_KEY, LS_CUSTOM_KEY, isoDate, periodToDates } from "./dateUtils";

const PERIODS = [
  { id: "7d", labelKey: "last_7d" },
  { id: "30d", labelKey: "last_30d" },
  { id: "90d", labelKey: "last_90d" },
  { id: "ytd", labelKey: "ytd" },
  { id: "custom", labelKey: "custom" },
];

// ── Styled components ──────────────────────────────────────────────────────────

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  justify-content: space-between;
`;

const LeftFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DateSection = styled.div`
  position: relative;
`;

const PeriodGroup = styled.div`
  display: flex;
  border: 1px solid ${colorFieldsBorder};
  border-radius: ${borderRadius};
  overflow: hidden;
`;

const PeriodBtn = styled.button`
  padding: 6px 14px;
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? "500" : "400")};
  color: ${({ $active }) => ($active ? "white" : colorTextAdditional)};
  background: ${({ $active }) => ($active ? sidenavColor : colorBackground)};
  border: none;
  border-right: 1px solid ${colorFieldsBorder};
  cursor: pointer;
  font-family: inherit;
  transition:
    background 0.12s,
    color 0.12s;

  &:last-child {
    border-right: none;
  }
  &:hover:not([disabled]) {
    background: ${({ $active }) => !$active && colorHover};
  }
`;

const CustomPickerPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 200;
  background: white;
  border: 1px solid ${colorLightGray};
  display: flex;
  flex-direction: column;
  width: 260px;
`;

const PickerBody = styled.div`
  padding: 10px 12px 6px;
  min-width: 200px;
`;

const ApplyRow = styled.div`
  padding: 8px 12px;
  display: flex;
  justify-content: center;
`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function FilterBar({ onChange }) {
  const { t } = useTranslation('common');
  const { groupId, setGroupId, vehicle, setVehicle, groups, vehicles } =
    useAuth();
  const savedCustom = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(LS_CUSTOM_KEY));
    } catch {
      return null;
    }
  }, []);

  const [period, setPeriod] = useState(
    () => localStorage.getItem(LS_KEY) || "90d",
  );
  const [showPicker, setShowPicker] = useState(false);

  // Date objects for the picker inputs
  const [fromDate, setFromDate] = useState(() =>
    savedCustom?.from ? new Date(savedCustom.from) : null,
  );
  const [toDate, setToDate] = useState(() =>
    savedCustom?.to ? new Date(savedCustom.to) : null,
  );

  // The dates currently in effect (what gets sent to onChange)
  const [appliedDates, setAppliedDates] = useState(() => {
    const p = localStorage.getItem(LS_KEY) || "90d";
    if (p === "custom" && savedCustom?.from && savedCustom?.to)
      return savedCustom;
    return periodToDates(p === "custom" ? "90d" : p);
  });

  // Close picker when clicking outside the DateSection
  const pickerRef = useClickAway({
    handleCloseElement: () => setShowPicker(false),
  });

  // vehicle_ids / group_ids to send in API requests
  const vehicle_ids = useMemo(() => {
    if (vehicle?.veh_id) return [String(vehicle.veh_id)];
    return [];
  }, [vehicle]);

  const group_ids = useMemo(() => {
    if (!vehicle?.veh_id && groupId && groupId !== "all-groups") {
      return [String(groupId)];
    }
    return [];
  }, [vehicle, groupId]);

  // Fire onChange only when appliedDates, vehicle_ids, or group_ids change
  useEffect(() => {
    onChange({ ...appliedDates, vehicle_ids, group_ids });
  }, [appliedDates, vehicle_ids, group_ids]);

  function handlePeriod(p) {
    //If custom is selected and picker is open, close it.
    if (p === "custom" && showPicker) {
      setShowPicker(false);
      return;
    }

    setPeriod(p);
    localStorage.setItem(LS_KEY, p);
    if (p === "custom") {
      setShowPicker(true);
      // No API call — wait for Apply
    } else {
      setShowPicker(false);
      setAppliedDates(periodToDates(p));
    }
  }

  function handleApply() {
    if (!fromDate || !toDate) return;
    const dates = { from: isoDate(fromDate), to: isoDate(toDate) };
    setAppliedDates(dates);
    localStorage.setItem(LS_CUSTOM_KEY, JSON.stringify(dates));
    setShowPicker(false);
  }

  function handleGroupChange(e) {
    setGroupId(e.value);
    setVehicle("");
  }

  const applyDisabled = !fromDate || !toDate;

  return (
    <Bar>
      <LeftFilters>
        <TreeSelect
          id="group-treeselect-dropdown"
          value={groupId}
          onChange={handleGroupChange}
          options={groups}
          placeholder={t('group')}
          filter
          className={
            groupId && groupId !== "all-groups" ? "treeselect-with-value" : ""
          }
        />

        <AssetDropdown
          assets={vehicles}
          vehicle={vehicle}
          onChange={setVehicle}
          isEquipmentSupported={true}
          placeholder={t('search_assets')}
        />
      </LeftFilters>

      <DateSection ref={pickerRef}>
        <PeriodGroup>
          {PERIODS.map((p) => (
            <PeriodBtn
              key={p.id}
              $active={period === p.id}
              onClick={() => handlePeriod(p.id)}
            >
              {t(p.labelKey)}
            </PeriodBtn>
          ))}
        </PeriodGroup>

        {showPicker && (
          <CustomPickerPanel>
            <PickerBody>
              <FormGroupLabel>{t('from')}</FormGroupLabel>
              <DatePicker
                selected={fromDate}
                onChange={setFromDate}
                customInput={<CustomInput />}
                dateFormat="P"
                showYearDropdown
                showMonthDropdown
                maxDate={toDate || new Date()}
              />
              <FormGroupLabel>{t('to')}</FormGroupLabel>
              <DatePicker
                selected={toDate}
                onChange={setToDate}
                customInput={<CustomInput />}
                dateFormat="P"
                showYearDropdown
                showMonthDropdown
                minDate={fromDate ? addDays(fromDate, 1) : null}
                maxDate={new Date()}
              />
            </PickerBody>
            <ApplyRow>
              <AddFormButton onClick={handleApply} disabled={applyDisabled}>
                {t('apply')}
              </AddFormButton>
            </ApplyRow>
          </CustomPickerPanel>
        )}
      </DateSection>
    </Bar>
  );

}
